import { useState, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight, Moon } from "lucide-react";
import { nextMonday, format } from "date-fns";
import { useItems, useSnoozeItems } from "@/hooks/useItems";
import { useSubtaskCounts } from "@/hooks/useSubtasks";
import { useProjectTree, type ProjectTreeNode } from "@/hooks/useProjects";
import { ItemCard } from "./ItemCard";
import { ItemActionSheet } from "./ItemActionSheet";
import { DispatchSheet } from "@/components/agent/DispatchSheet";
import { Button } from "@/components/ui/button";
import type { Item, Project } from "@/lib/types";

function ProjectGroup({
  label,
  color,
  items,
  subtaskCounts,
  onItemClick,
  onDispatch,
  children,
  defaultOpen,
  totalOverride,
}: {
  label: string;
  color?: string;
  items: Item[];
  subtaskCounts: Record<string, { done: number; total: number }> | undefined;
  onItemClick: (item: Item) => void;
  onDispatch: (item: Item) => void;
  children?: React.ReactNode;
  defaultOpen?: boolean;
  totalOverride?: number;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const snoozeAll = useSnoozeItems();
  const monday = format(nextMonday(new Date()), "yyyy-MM-dd");

  const totalCount = totalOverride ?? items.length;

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full text-left py-2 px-1 hover:bg-muted/50 rounded transition-colors"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
        <div
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: color || "#888" }}
        />
        <span className="text-sm font-medium flex-1">{label}</span>
        <span className="text-xs text-muted-foreground">{totalCount}</span>
        {totalCount > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            title="Snooze all to next Monday"
            onClick={(e) => {
              e.stopPropagation();
              snoozeAll.mutate({ ids: items.map((i) => i.id), date_target: monday });
            }}
          >
            <Moon className="h-3 w-3" />
          </Button>
        )}
      </button>
      {open && (
        <div className="ml-4">
          {/* Child project sub-groups */}
          {children}
          {/* Direct items */}
          {items.length > 0 && (
            <div className="space-y-1">
              <AnimatePresence mode="popLayout">
                {items.map((item) => (
                  <ItemCard
                    key={item.id}
                    item={item}
                    compact
                    projectColor={color}
                    hideTag
                    onClick={() => onItemClick(item)}
                    onDispatch={onDispatch}
                    subtaskCount={subtaskCounts?.[item.id]}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ItemList() {
  const { data: items, isLoading } = useItems();
  const { data: tree, projects } = useProjectTree();
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [dispatchItem, setDispatchItem] = useState<Item | null>(null);

  const itemIds = items?.map((i) => i.id) ?? [];
  const { data: subtaskCounts } = useSubtaskCounts(itemIds);

  // Build a project lookup (name/slug -> project)
  const projectMap = useMemo(() => {
    const m = new Map<string, Project>();
    projects?.forEach((p) => {
      m.set(p.name, p);
      m.set(p.slug, p);
    });
    return m;
  }, [projects]);


  // Split into focus zone (top 5) and the rest, grouped hierarchically
  const { focusItems, parentGroups, ungroupedItems } = useMemo(() => {
    if (!items || items.length === 0)
      return { focusItems: [], parentGroups: [], ungroupedItems: [] };

    // Pick top 5: high priority first, then by urgency_score, then oldest
    const sorted = [...items].sort((a, b) => {
      const po = { high: 0, medium: 1, low: 2 };
      const pa = po[a.priority] ?? 1;
      const pb = po[b.priority] ?? 1;
      if (pa !== pb) return pa - pb;
      // Higher urgency_score = more urgent
      const ua = a.urgency_score ?? 50;
      const ub = b.urgency_score ?? 50;
      if (ua !== ub) return ub - ua;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });

    const focus = sorted.slice(0, 5);
    const focusIds = new Set(focus.map((i) => i.id));
    const rest = items.filter((i) => !focusIds.has(i.id));

    // Group rest by project hierarchy
    // 1. Find which parent each item belongs to
    const itemsByParent = new Map<string, { parentProject: ProjectTreeNode; childItems: Map<string, Item[]>; directItems: Item[] }>();
    const ungrouped: Item[] = [];

    for (const item of rest) {
      const tag = item.project_tag;
      if (!tag) {
        ungrouped.push(item);
        continue;
      }

      const proj = projectMap.get(tag);
      if (!proj) {
        ungrouped.push(item);
        continue;
      }

      // Find if this project is a child or a parent
      if (proj.parent_id) {
        // It's a child project — find its parent in the tree
        const parentNode = tree?.find((t) => t.id === proj.parent_id);
        if (parentNode) {
          if (!itemsByParent.has(parentNode.id)) {
            itemsByParent.set(parentNode.id, { parentProject: parentNode, childItems: new Map(), directItems: [] });
          }
          const group = itemsByParent.get(parentNode.id)!;
          if (!group.childItems.has(proj.id)) group.childItems.set(proj.id, []);
          group.childItems.get(proj.id)!.push(item);
        } else {
          ungrouped.push(item);
        }
      } else {
        // It's a parent project — check if the tree has it
        const parentNode = tree?.find((t) => t.id === proj.id);
        if (parentNode) {
          if (!itemsByParent.has(parentNode.id)) {
            itemsByParent.set(parentNode.id, { parentProject: parentNode, childItems: new Map(), directItems: [] });
          }
          itemsByParent.get(parentNode.id)!.directItems.push(item);
        } else {
          ungrouped.push(item);
        }
      }
    }

    const groups = Array.from(itemsByParent.values()).sort((a, b) =>
      a.parentProject.name.localeCompare(b.parentProject.name)
    );

    return { focusItems: focus, parentGroups: groups, ungroupedItems: ungrouped };
  }, [items, projectMap, tree]);

  if (isLoading) {
    return <div className="text-muted-foreground text-sm">Loading items...</div>;
  }

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-16 space-y-3">
        <div className="text-2xl font-mono font-bold tracking-wider text-muted-foreground/60">
          ALL OBJECTIVES CLEARED
        </div>
        <p className="text-sm text-muted-foreground/40 tracking-wide">Stand down.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Focus Zone */}
      {focusItems.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">
            Pick one ({focusItems.length})
          </h3>
          <div className="space-y-2">
            <AnimatePresence mode="popLayout">
              {focusItems.map((item) => {
                const proj = item.project_tag ? projectMap.get(item.project_tag) : undefined;
                return (
                  <ItemCard
                    key={item.id}
                    item={item}
                    projectColor={proj?.color}
                    hideTag
                    onClick={() => setSelectedItem(item)}
                    onDispatch={setDispatchItem}
                    subtaskCount={subtaskCounts?.[item.id]}
                  />
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Hierarchical project groups */}
      {(parentGroups.length > 0 || ungroupedItems.length > 0) && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">
            Everything else (
            {parentGroups.reduce(
              (s, g) =>
                s +
                g.directItems.length +
                Array.from(g.childItems.values()).reduce((c, arr) => c + arr.length, 0),
              0
            ) + ungroupedItems.length}
            )
          </h3>
          <div className="space-y-1">
            {parentGroups.map((group) => {
              const totalCount =
                group.directItems.length +
                Array.from(group.childItems.values()).reduce((c, arr) => c + arr.length, 0);

              return (
                <ProjectGroup
                  key={group.parentProject.id}
                  label={group.parentProject.name}
                  color={group.parentProject.color}
                  items={group.directItems}
                  subtaskCounts={subtaskCounts}
                  onItemClick={setSelectedItem}
                  onDispatch={setDispatchItem}
                  totalOverride={totalCount}
                >
                  {/* Child project sub-groups */}
                  {Array.from(group.childItems.entries()).map(([childId, childItems]) => {
                    const childProj = projects?.find((p) => p.id === childId);
                    if (!childProj || childItems.length === 0) return null;
                    return (
                      <ProjectGroup
                        key={childId}
                        label={childProj.name}
                        color={childProj.color}
                        items={childItems}
                        subtaskCounts={subtaskCounts}
                        onItemClick={setSelectedItem}
                        onDispatch={setDispatchItem}
                      />
                    );
                  })}
                </ProjectGroup>
              );
            })}

            {ungroupedItems.length > 0 && (
              <ProjectGroup
                key="__ungrouped__"
                label="Ungrouped"
                items={ungroupedItems}
                subtaskCounts={subtaskCounts}
                onItemClick={setSelectedItem}
                onDispatch={setDispatchItem}
              />
            )}
          </div>
        </div>
      )}

      <ItemActionSheet
        item={selectedItem}
        open={!!selectedItem}
        onOpenChange={(open) => !open && setSelectedItem(null)}
      />
      <DispatchSheet
        item={dispatchItem}
        open={!!dispatchItem}
        onOpenChange={(open) => !open && setDispatchItem(null)}
      />
    </div>
  );
}
